import { z } from 'zod';

import type { FieldValidationError } from './types.js';

const fieldErrorSchema = z.object({
  field: z.string(),
  code: z.string(),
  message: z.string(),
});

const problemDetailsSchema = z.looseObject({
  type: z.string().optional(),
  title: z.string().optional(),
  status: z.number().optional(),
  detail: z.string().optional(),
  code: z.string().optional(),
  correlationId: z.string().optional(),
  errors: z.array(fieldErrorSchema).optional(),
});

export function parseProblemDetails(
  body: unknown,
  fallbackStatus: number,
): {
  code: string;
  message: string;
  correlationId?: string;
  fieldErrors?: FieldValidationError[];
  details?: Record<string, unknown>;
} {
  const parsed = problemDetailsSchema.safeParse(body);

  if (!parsed.success) {
    return {
      code: 'HTTP_ERROR',
      message: `Request failed with status ${fallbackStatus}`,
    };
  }

  const data = parsed.data;
  const code = data.code ?? 'HTTP_ERROR';
  const message = data.detail ?? data.title ?? `Request failed with status ${fallbackStatus}`;

  const result: {
    code: string;
    message: string;
    correlationId?: string;
    fieldErrors?: FieldValidationError[];
    details?: Record<string, unknown>;
  } = {
    code,
    message,
  };

  if (data.correlationId !== undefined) {
    result.correlationId = data.correlationId;
  }

  if (data.errors !== undefined) {
    result.fieldErrors = data.errors;
  }

  const {
    type: _type,
    title: _title,
    status: _status,
    detail: _detail,
    code: _code,
    correlationId: _correlationId,
    errors: _errors,
    ...rest
  } = data;
  if (Object.keys(rest).length > 0) {
    result.details = rest;
  }

  return result;
}
